import { memo, useCallback } from 'react';

import {
  type AirActionTrackingLocation,
  type TrackCopiedShareLinkParams,
  useTrackCopiedShareLink,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { CopyLinkButton } from '~/components/ShareModal/components/ShareLink/CopyLinkButton';
import { ShareLink } from '~/components/ShareModal/components/ShareLink/ShareLink';
import { getFullShareUrl } from '~/utils/shareUrl';
import { buildShareUrlCopiedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlCopiedAnalyticsParams';

export type ReadonlyShareLinkProps = {
  objectId: string;
  trackLocation: AirActionTrackingLocation;
  searchParams?: URLSearchParams;
  shareId: ShortUrlResponse['id'];
  shortIdInfo: ShortUrlResponse;
  objectType: TrackCopiedShareLinkParams['type'];
};

export const ReadonlyShareLink = memo(
  ({ objectId, trackLocation, searchParams, shareId, shortIdInfo, objectType }: ReadonlyShareLinkProps) => {
    const { trackCopiedShareLink } = useTrackCopiedShareLink();

    const shareUrl = getFullShareUrl(shareId, searchParams);

    const onCopied = useCallback(() => {
      trackCopiedShareLink({
        id: objectId,
        shareId,
        type: objectType,
        location: trackLocation,
        ...buildShareUrlCopiedAnalyticsParams({ shareId, shortUrl: shortIdInfo, objectType }),
      });
    }, [trackCopiedShareLink, objectId, shareId, objectType, trackLocation, shortIdInfo]);

    return (
      <div className="flex gap-2">
        <ShareLink shareUrl={shareUrl} />
        <CopyLinkButton shareUrl={shareUrl} onCopied={onCopied} />
      </div>
    );
  },
);

ReadonlyShareLink.displayName = 'ReadonlyShareLink';
